import { CookieBar } from "components/CookieBar";
import { EN, NL } from "shared/constants";
import { Footer } from "components/Footer";
import { Header } from "components/Header";
import { NewsletterSection } from "components/NewsletterSection";
import { useAppContext } from "shared/useAppContext";
import { useRouter } from "next/router";
import Head from "next/head";
import styles from "./Page.module.css";
import cx from "classnames";

export const Page = ({
  children,
  darkHeader,
  description,
  footer,
  footerHasBorder,
  hasNewsletter,
  hasRelated,
  image,
  mirrorPath,
  navigation,
  title,
}) => {
  const { cookies } = useAppContext();
  const { accepted, setAccepted, setAdvertising } = cookies;

  const { locale } = useRouter();
  const titleToUse = title
    ? title.indexOf("Clariah") !== title.length - 7
      ? `${title} - Clariah`
      : title
    : "Clariah";
  const imageToUse = image
    ? `${image}?w=1200&h=630&fit=crop`
    : `${process.env.SANITY_STUDIO_SITE_ROOT}/og-image.png`;

  return (
    <div className={styles.container}>
      <Head>
        <title>{titleToUse}</title>
        <meta name="title" content={title} />
        {description && <meta name="description" content={description} />}
        <meta property="og:title" content={titleToUse} />
        {description && (
          <meta property="og:description" content={description} />
        )}
        <meta property="og:type" content="website" />
        {imageToUse && <meta property="og:image" content={imageToUse} />}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:creator" content="@clariah" />
        <meta property="twitter:title" content={titleToUse} />
        {description && (
          <meta property="twitter:description" content={description} />
        )}
        {imageToUse && <meta property="twitter:image" content={imageToUse} />}

        {mirrorPath && (
          <link
            rel="alternate"
            hrefLang={locale === NL ? EN : NL}
            href={mirrorPath}
          />
        )}

        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/favicons/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicons/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicons/favicon-16x16.png"
        />
        <link rel="manifest" href="/favicons/site.webmanifest" />
        <link
          rel="mask-icon"
          href="/favicons/safari-pinned-tab.svg"
          color="#2350ff"
        />
        <link rel="shortcut icon" href="/favicons/favicon.ico" />
        <meta name="msapplication-TileColor" content="#ffffff" />
        <meta
          name="msapplication-config"
          content="/favicons/browserconfig.xml"
        />
        <meta name="theme-color" content="#ffffff" />

        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous"
        />

        <link
          rel="preload"
          as="style"
          href="https://fonts.googleapis.com/css2?family=PT+Serif:ital,wght@0,400;0,700;1,400&display=swap"
        />

        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=PT+Serif:ital,wght@0,400;0,700;1,400&display=swap"
          media="print"
          onLoad="this.media='all'"
        />

        <script
          async
          src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS}`}
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS}', { 'anonymize_ip': true });
            `,
          }}
        />
      </Head>
      <div
        dangerouslySetInnerHTML={{
          __html: `
              <!--
                Design and development:   Eight Media, User Experience Design Agency
                Contact:                  hello@eight.nl
              -->
            `,
        }}
      />
      <Header
        isDark={darkHeader}
        mirrorPath={mirrorPath}
        navigation={navigation}
      />
      <div
        className={cx(styles.childrenContainer, {
          [styles.noSpacingBottom]: !hasNewsletter || hasRelated,
        })}
        id="content"
      >
        {children}
      </div>
      {hasNewsletter && <NewsletterSection />}
      <Footer footer={footer} withBorder={footerHasBorder} />
      {!accepted && (
        <CookieBar setAccepted={setAccepted} setAdvertising={setAdvertising} />
      )}
    </div>
  );
};
